import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { VisitorWorkflowConfig } from 'types/visitorWorkflowTypes';
import { getRequest } from 'api/apiClient';
import Loader from 'components/customAntd/Loader';
import { useTranslation } from 'react-i18next';
import { DATE_TIME_FORMAT } from 'constants/dates';
import dayjs from 'dayjs';
import STATUS from 'constants/status';

interface SignInMethodModalProps {
    open: boolean;
    onClose: () => void;
    openSignInModal: () => void;
    openScanIDModal: () => void;
    openScanQRCodeModal: () => void;
    openSignInManuallyDrawer: () => void;
    locationIdRef: number;
}

const SignInMethodModal: React.FC<SignInMethodModalProps> = ({ open, onClose, openSignInModal, openScanIDModal, openScanQRCodeModal, openSignInManuallyDrawer, locationIdRef }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);

    const [loading, setLoading] = useState<boolean>(false);

    const { invitationConfig } = useSelector((state: RootState) => state.visitorInvitation);
    const [signInWorkflowConfig, setSignInWorkflowConfig] = useState<VisitorWorkflowConfig>();

    const { t } = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const locationWorkflowResponse = await getRequest(`/orgs/${orgId}/sites/${locationIdRef}/workflows`);
                const signInWorkflowConfigResponse = await getRequest(`/orgs/${orgId}/visitorWorkflowConfig/${locationWorkflowResponse.data[0].SignInWorkflowId}`);
                setSignInWorkflowConfig(signInWorkflowConfigResponse.data[0]);
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [dispatch, orgId, locationIdRef]);

    return (
        <Modal
            title={t('signInMethodModal.title')}
            open={open}
            onOk={onClose}
            onCancel={onClose}
            centered
            footer={null}
        >
            <OpSpace direction="vertical" size="large" style={{ display: 'flex', paddingTop: '16px' }}>
                {loading ? (<Loader />) : (
                    <>
                        <OpButton
                            type="default"
                            style={{ width: '100%', backgroundColor: 'rgba(19,151,213,0.1)' }}
                            onClick={async () => {
                                try {
                                    const response = await getRequest(`orgs/${orgId}/search/visitors`, {
                                        siteId: locationIdRef,
                                        startDate: dayjs().startOf('day').format(DATE_TIME_FORMAT),
                                        endDate: dayjs().endOf('day').format(DATE_TIME_FORMAT),
                                    });

                                    const pendingVisitors = response.data.filter((visitor: any) => visitor.status === STATUS.PENDING.id);

                                    if (pendingVisitors.length === 0) { // If there are no pending visits → open the “Add Visitor” modal and drawer
                                        openSignInManuallyDrawer(); 
                                    } else {
                                        openSignInModal();
                                    }
                                } catch (error) {
                                    console.error('Failed to fetch visitors', error);
                                    openSignInManuallyDrawer();
                                }
                                onClose();
                            }}
                        >
                            {t('signInMethodModal.selectOrEnter')}
                        </OpButton>
                        {((signInWorkflowConfig?.scan === 1) && (signInWorkflowConfig?.scanScanner === 1)) && (
                            <OpButton
                                type="default"
                                style={{ width: '100%', backgroundColor: 'rgba(19,151,213,0.1)' }}
                                onClick={() => {
                                    openScanIDModal();
                                    onClose();
                                }}
                            >
                                {t('signInMethodModal.scanId')}
                            </OpButton>
                        )}
                        {(invitationConfig?.allowPreRegistration === 1) && (
                            <OpButton
                                type="default"
                                style={{ width: '100%', backgroundColor: 'rgba(19,151,213,0.1)' }}
                                onClick={() => {
                                    openScanQRCodeModal();
                                    onClose();
                                }}
                            >
                                {t('signInMethodModal.scanQrCode')}
                            </OpButton>
                        )}
                    </>
                )}
            </OpSpace>
        </Modal>
    );
};

export default SignInMethodModal;
